/**
 * File Storage Models and Types
 *
 * TypeScript interfaces for File Storage API v0.1
 * Matches backend response structures from Go backend
 */

// File category type
// 'docs': miscellaneous files unrelated to other categories (no folder view)
// 'nvr': NVR recordings / exports (no folder view)
// 'test': retained for legacy ParkingTest/LearningData flows — not exposed in
//   the file repository tab UI but still used by parking-test storage paths.
// 'cctv' — CCTV 1대당 배경 이미지 1장 ({cctvId}.jpg). ROI 편집기가 하드코딩
// 템플릿 없이도 배경을 얻는 경로다 (백엔드 entity.CategoryCctv 와 일치).
export type FileCategory = 'map' | 'cad' | 'csv' | 'roi' | 'learning' | 'docs' | 'nvr' | 'test' | 'pdf' | 'cctv';

// File status type
export type FileStatus = 'draft' | 'production' | 'deprecated';

// File status labels for UI display
export const FILE_STATUS_LABELS: Record<FileStatus, string> = {
  draft: '임시',
  production: '적용 중',
  deprecated: '사용 중단'
};

// File status colors for UI display
export const FILE_STATUS_COLORS: Record<FileStatus, 'default' | 'success' | 'error'> = {
  draft: 'default',
  production: 'success',
  deprecated: 'error'
};

/**
 * File information structure
 * Matches backend entity: backend/src/features/filestorage/model/entity/fileInfo.go
 */
export interface FileInfo {
  filename: string;           // Actual filename with version (e.g., "parking_map_1234567890.json")
  original_name: string;      // Original filename without version (e.g., "parking_map.json")
  version: string;            // Version timestamp (e.g., "1234567890")
  size_bytes: number;         // File size in bytes
  upload_date: string;        // Upload date in ISO 8601 format
  file_type: string;          // MIME type (e.g., "application/json")
  path: string;               // Server file path
  cctv_id?: string;           // CCTV ID (only for learning/test categories)
  source_file_id?: number;    // Source file ID (for CSV-DXF relationship)
  source_category?: string;   // Source file category (e.g., "cad")
  uploaded_by_name?: string;  // Name of user who uploaded the file
  description?: string;        // File description/notes
  status?: FileStatus;         // File status: draft, production, deprecated
}

/**
 * Upload response structure
 * Matches backend response: backend/src/features/filestorage/model/response/upload.go
 */
export interface UploadResponse {
  success: boolean;
  message: string;
  data: {
    total_files: number;
    success_count: number;
    failed_count: number;
    version?: string;
    uploaded_files: FileInfo[];
    errors?: string[];
  };
}

/**
 * File list response structure with pagination
 * Matches backend response: backend/src/features/filestorage/model/response/fileList.go
 */
export interface FileListResponse {
  success: boolean;
  message: string;
  data: {
    total_count: number;
    files: FileInfo[];
    pagination: {
      page: number;
      page_size: number;
      total_pages: number;
    };
  };
}

/**
 * Auto-upload response structure (202 Accepted)
 * Used for map editor auto-save feature
 */
export interface AutoUploadResponse {
  success: boolean;
  message: string;
  data: {
    project_id: string;
    filename: string;
  };
}

/**
 * File repository state interface
 * Used by FileRepositoryViewModel for state management
 */
export interface FileRepositoryState {
  currentCategory: FileCategory;
  files: FileInfo[];
  folders: FolderNode[];      // Folder/file list for learning/test categories
  selectedFolder: FolderNode | null;  // Currently selected folder
  currentPath: string;         // Current folder path for nested navigation
  viewMode: 'folders' | 'files';  // View mode: folders or files
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
  };
  selectedCctvId?: string;    // For learning/test category filtering
  uploadProgress: number;     // Upload progress percentage (0-100)
}

/**
 * Auto-save state interface
 * Used by MapEditorAutoSaveViewModel
 */
export interface AutoSaveState {
  saving: boolean;            // Currently saving
  success: boolean | null;    // Save result (null = not attempted, true = success, false = failed)
  error: string | null;       // Error message if failed
}

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Chunk upload options
 */
export interface ChunkUploadOptions {
  projectId: string;
  category: FileCategory;
  file: File;
  chunkSize?: number;         // Chunk size in bytes (default: 5MB)
  onProgress?: (progress: number) => void;
  onChunkComplete?: (chunkIndex: number, totalChunks: number) => void;
}

/**
 * Folder/File node structure for learning/test categories (original)
 */
export interface FolderNode {
  name: string;               // Folder/file name
  path: string;               // Full path relative to category
  isFolder: boolean;          // true = folder, false = file
  count?: number;             // Number of items inside (for folders)
  size?: number;              // File size in bytes (for files)
  uploadDate?: string;        // Upload date (for files)
}

/**
 * File node structure (for nested folder API response)
 */
export interface FileNode {
  name: string;
  size: number;
  created_at: string;
}

/**
 * Nested folder node structure (for learning data management)
 */
export interface NestedFolderNode {
  name: string;               // Folder name
  path: string;               // Full path relative to category
  files: FileNode[];          // Files in this folder
  subfolders?: NestedFolderNode[];  // Subfolders (for learning category)
  file_count: number;         // Total file count
  created_at: string;         // Creation timestamp
}

/**
 * Folder list response (old format for FileRepository)
 */
export interface FolderListResponse {
  success: boolean;
  message: string;
  data: {
    items: FolderNode[];      // List of folders and files
    total: number;
    currentPath: string;       // Current folder path
  };
}

/**
 * Nested folder list response (for learning data management)
 */
export interface NestedFolderListResponse {
  success: boolean;
  message: string;
  data: {
    folders: NestedFolderNode[];    // List of folders with nested structure
  };
}
